import { ArrowLeft } from "lucide-react";
import { type ComponentType, useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/common/loader";
import type { Client } from "@/features/clients/types";
import { getOrCreateSubmission, getSubmission, listTaxForms } from "@/features/forms/api";
import { Form2848Editor } from "@/features/forms/components/form-2848-editor";
import { Form3520Editor } from "@/features/forms/components/form-3520-editor";
import { Form8821Editor } from "@/features/forms/components/form-8821-editor";
import { Form2848spEditor } from "@/features/forms/components/form-2848sp-editor";
import { Form2848aEditor } from "@/features/forms/components/form-2848a-editor";
import { Form2848dcEditor } from "@/features/forms/components/form-2848dc-editor";
import { Form2848ilEditor } from "@/features/forms/components/form-2848il-editor";
import { Form2848riEditor } from "@/features/forms/components/form-2848ri-editor";
import { Form_ia2848_Editor } from "@/features/forms/components/form-ia2848-editor";
import { FormIdpoaEditor } from "@/features/forms/components/form-idpoa-editor";
import { FormM2848Editor } from "@/features/forms/components/form-m2848-editor";
import { FormMipoaEditor } from "@/features/forms/components/form-mipoa-editor";
import { FormOrauthinfoEditor } from "@/features/forms/components/form-orauthinfo-editor";
import { FormOrauthrepEditor } from "@/features/forms/components/form-orauthrep-editor";
import { FormPar101Editor } from "@/features/forms/components/form-par101-editor";
import { FormPoa1Editor } from "@/features/forms/components/form-poa1-editor";
import { FormSc2848Editor } from "@/features/forms/components/form-sc2848-editor";
import { FormWv2848Editor } from "@/features/forms/components/form-wv2848-editor";
import { Form433AttachmentEditor } from "@/features/forms/components/form-433-attachment-editor";
import { Form433DEditor } from "@/features/forms/components/form-433d-editor";
import { Form433HEditor } from "@/features/forms/components/form-433h-editor";
import { Form9465Editor } from "@/features/forms/components/form-9465-editor";
import { Form2159Editor } from "@/features/forms/components/form-2159-editor";
import type { FormSubmission } from "@/features/forms/types";
import { Form8379Wizard } from "@/features/workflows/innocent-spouse/components/form-8379-wizard";
import { SpouseReliefWizard } from "@/features/workflows/spouse-relief/spouse-relief-wizard";
import { useActiveClient } from "@/layouts/client-layout";
import { ApiError } from "@/lib/api";

export interface FormEditorProps {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

// Per-form fill editors. Add an entry (+ backend mapper + GENERATABLE_FORMS)
// when a new form gains a fill UI.
const EDITORS: Record<string, ComponentType<FormEditorProps>> = {
  "2848": Form2848Editor,
  "3520": Form3520Editor,
  // POA / TIA family (milestone 03-2).
  "8821": Form8821Editor, // Tax Information Authorization — disclosure-only.
  "2848sp": Form2848spEditor,
  "2848a": Form2848aEditor,
  "2848dc": Form2848dcEditor,
  "2848il": Form2848ilEditor,
  "2848ri": Form2848riEditor,
  ia2848: Form_ia2848_Editor,
  m2848: FormM2848Editor,
  sc2848: FormSc2848Editor,
  wv2848: FormWv2848Editor,
  mipoa: FormMipoaEditor,
  idpoa: FormIdpoaEditor,
  orauthinfo: FormOrauthinfoEditor,
  orauthrep: FormOrauthrepEditor,
  par101: FormPar101Editor,
  poa1: FormPoa1Editor,
  // Injured Spouse Allocation — the ISA wizard is the 8379 editor (03-15). It's
  // also reachable at /client/:id/workflows/innocent-spouse (same submission).
  "8379": Form8379Wizard,
  // Request for Innocent Spouse Relief — the Spouse-Relief wizard is the 8857
  // editor (03-16); also at /client/:id/workflows/spouse-relief (same submission).
  // No caseId here → Part IV assets are managed from a Case.
  "8857": SpouseReliefWizard,
  // Installment-Agreement request family (milestone 03-3). Spanish twins
  // (9465sp/2159sp) are deferred pending their blank IRS PDF templates.
  "9465": Form9465Editor,
  "2159": Form2159Editor,
  // 433 family (milestone 03-1): D/H have editors; the rest are attachment-style.
  "433-D": Form433DEditor,
  "433-D (SP)": Form433DEditor,
  "433-H": Form433HEditor,
  "433-H (SP)": Form433HEditor,
  "433-A": Form433AttachmentEditor,
  "433-A (SP)": Form433AttachmentEditor,
  "433-A (OIC)": Form433AttachmentEditor,
  "433-B": Form433AttachmentEditor,
  "433-B (SP)": Form433AttachmentEditor,
  "433-B (OIC)": Form433AttachmentEditor,
  "433-F": Form433AttachmentEditor,
  "433-F (SP)": Form433AttachmentEditor,
};

/**
 * Generic fill page for a client's IRS form (`/client/:id/irs-forms/:formNumber`):
 * resolves (or creates) the prefilled submission and renders the form-specific
 * editor for Save / Preview / Save-to-Documents. Unknown forms bounce to the list.
 */
export default function ClientFormFillPage() {
  const { client } = useActiveClient();
  const { formNumber = "" } = useParams<{ formNumber: string }>();
  const Editor = EDITORS[formNumber];

  const [submission, setSubmission] = useState<FormSubmission | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  // Discards a superseded load if the user navigates between forms quickly (an
  // older in-flight chain must not stomp the newer form's submission/title).
  const requestIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!EDITORS[formNumber]) return; // handled by the redirect below
    const reqId = ++requestIdRef.current;
    const fresh = () => mountedRef.current && requestIdRef.current === reqId;
    setLoading(true);
    setError(null);
    listTaxForms()
      .then((forms) => {
        const taxForm = forms.find((f) => f.formNumber === formNumber);
        if (!taxForm) throw new ApiError(404, `Form ${formNumber} is not available.`, null);
        if (fresh()) setTitle(taxForm.title);
        return getOrCreateSubmission(client.id, taxForm.id);
      })
      // Retrieve marks the form recently-opened (bumps last_opened_at → Recent).
      .then((sub) => getSubmission(sub.id))
      .then((sub) => {
        if (fresh()) setSubmission(sub);
      })
      .catch((err) => {
        if (fresh()) {
          setError(err instanceof ApiError ? err.message : "Couldn't open this form.");
        }
      })
      .finally(() => {
        if (fresh()) setLoading(false);
      });
  }, [client.id, formNumber]);

  useEffect(load, [load]);

  if (!Editor) return <Navigate to={`/client/${client.id}/irs-forms`} replace />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to={`/client/${client.id}/irs-forms`}>
            <ArrowLeft /> Back to Forms
          </Link>
        </Button>
        <h2 className="text-foreground text-2xl font-bold">
          Form {formNumber}
          {title ? ` — ${title}` : ""}
        </h2>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error || !submission ? (
        <p className="text-destructive py-8 text-sm">{error ?? "Couldn't open this form."}</p>
      ) : (
        <Editor client={client} submission={submission} onSubmissionChange={setSubmission} />
      )}
    </div>
  );
}
